const META_EVENTS = new Set(["PageView", "ViewContent", "AddPaymentInfo", "AddToCart", "InitiateCheckout", "Lead", "Purchase", "Contact", "Subscribe"]);
const TIKTOK_EVENTS = new Set(["ViewContent", "AddToCart", "InitiateCheckout", "AddPaymentInfo", "PlaceAnOrder", "CompletePayment", "SubmitForm", "Contact", "ClickButton"]);

const loadedMetaPixels = new Set();
const loadedTiktokPixels = new Set();
const loadedGoogleAdsTags = new Set();

export function markLandingMetaPixelLoaded(pixelId) {
  if (pixelId) loadedMetaPixels.add(String(pixelId));
}

export function markLandingTiktokPixelLoaded(pixelId) {
  if (pixelId) loadedTiktokPixels.add(String(pixelId));
}

function ensureMetaPixel(pixelId) {
  if (!pixelId || typeof window === "undefined") return null;
  if (typeof window.fbq !== "function") {
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  }
  if (!loadedMetaPixels.has(pixelId)) {
    window.fbq("init", pixelId);
    loadedMetaPixels.add(pixelId);
  }
  return window.fbq;
}

function ensureTiktokPixel(pixelId) {
  if (!pixelId || typeof window === "undefined") return null;
  if (!window.ttq || typeof window.ttq.track !== "function") {
    !(function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      const ttq = (w[t] = w[t] || []);
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
      ttq.setAndDefer = function (obj, method) { obj[method] = function () { obj.push([method].concat(Array.prototype.slice.call(arguments, 0))); }; };
      for (let i = 0; i < ttq.methods.length; i += 1) ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (id) { const inst = ttq._i[id] || []; for (let i = 0; i < ttq.methods.length; i += 1) ttq.setAndDefer(inst, ttq.methods[i]); return inst; };
      ttq.load = function (id, options) { const src = "https://analytics.tiktok.com/i18n/pixel/events.js"; ttq._i = ttq._i || {}; ttq._i[id] = []; ttq._i[id]._u = src; ttq._t = ttq._t || {}; ttq._t[id] = +new Date(); ttq._o = ttq._o || {}; ttq._o[id] = options || {}; const script = d.createElement("script"); script.type = "text/javascript"; script.async = true; script.src = `${src}?sdkid=${id}&lib=${t}`; const firstScript = d.getElementsByTagName("script")[0]; firstScript.parentNode.insertBefore(script, firstScript); };
    })(window, document, "ttq");
  }
  if (!loadedTiktokPixels.has(pixelId)) {
    if (typeof window.ttq.load !== "function") return null;
    window.ttq.load(pixelId);
    loadedTiktokPixels.add(pixelId);
  }
  return typeof window.ttq.instance === "function" ? window.ttq.instance(pixelId) : window.ttq;
}

function ensureGoogleAdsTag(conversionId) {
  if (!conversionId || typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  if (!loadedGoogleAdsTags.has(conversionId)) {
    const scriptId = `landing-google-ads-${conversionId}`;
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(conversionId)}`;
      document.head.appendChild(script);
    }
    window.gtag("js", new Date());
    window.gtag("config", conversionId);
    loadedGoogleAdsTags.add(conversionId);
  }
  return window.gtag;
}

export function trackLandingAdsButtonClick({ analytics = {}, button, clickPayload = {}, landingSlug, landingTitle }) {
  if (button?.enabled === false || typeof window === "undefined") return [];
  const fired = [];
  const commonPayload = {
    content_name: landingTitle || landingSlug,
    button_key: String(clickPayload.key || ""),
    button_label: String(clickPayload.label || ""),
    button_href: String(clickPayload.href || ""),
    landing_slug: landingSlug,
  };

  const facebook = analytics.facebook || {};
  const facebookPixelId = String(button?.facebookPixelId || (facebook.enabled ? facebook.pixelId : "") || "").replace(/[^0-9]/g, "").slice(0, 32);
  const facebookEvent = META_EVENTS.has(String(button?.facebookEvent || "Lead")) ? String(button?.facebookEvent || "Lead") : "Lead";
  if (facebookPixelId) {
    const fbq = ensureMetaPixel(facebookPixelId);
    if (typeof fbq === "function") {
      fbq("trackSingle", facebookPixelId, facebookEvent, commonPayload);
      fired.push("meta");
    }
  }

  const tiktok = analytics.tiktok || {};
  const tiktokPixelId = String(button?.tiktokPixelId || (tiktok.enabled ? tiktok.pixelId : "") || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 64);
  const tiktokEvent = TIKTOK_EVENTS.has(String(button?.tiktokEvent || "ClickButton")) ? String(button?.tiktokEvent || "ClickButton") : "ClickButton";
  if (tiktokPixelId) {
    const ttq = ensureTiktokPixel(tiktokPixelId);
    if (ttq && typeof ttq.track === "function") {
      ttq.track(tiktokEvent, commonPayload);
      fired.push("tiktok");
    }
  }

  const google = analytics.google || {};
  const rawGoogleAdsId = String(button?.googleAdsId || google.googleAdsId || "").trim().toUpperCase();
  const normalizedGoogleAdsId = /^\d+$/.test(rawGoogleAdsId) ? `AW-${rawGoogleAdsId}` : rawGoogleAdsId;
  const googleAdsId = /^AW-\d+$/.test(normalizedGoogleAdsId) ? normalizedGoogleAdsId : "";
  const googleAdsLabel = String(button?.googleAdsLabel || google.googleAdsLabel || "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 100);
  if (googleAdsId && googleAdsLabel) {
    const gtag = ensureGoogleAdsTag(googleAdsId);
    if (typeof gtag === "function") {
      gtag("event", "conversion", { send_to: `${googleAdsId}/${googleAdsLabel}`, ...commonPayload });
      fired.push("google_ads");
    }
  }

  if (google.enabled && String(google.gtmId || "").trim()) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "landing_button_click", ...commonPayload });
    fired.push("google_gtm");
  }

  return fired;
}
